import { ARCH_HEIGHT_DATA, ARCH_HEIGHT_DATA_ORDERED, FOOT_SIZE_DATA, WEIGHT_DATA } from '../constants/foot';
import { ArchHeightData, FootSizeData } from '../types/foot';
import type { QueryParams } from '../hooks/useQueryParams';
import type { CustomInsoleValues } from '../context/AppContext';

/**
 * Gets arch height data based on measurement in mm
 * @param archHeightInMM - Arch height measurement in millimeters
 * @returns ArchHeightData object matching the measurement
 */
export const getArchHeightData = (archHeightInMM: number): ArchHeightData => {
  const archData = ARCH_HEIGHT_DATA_ORDERED.find(
    (data) => archHeightInMM > data.greaterThan && archHeightInMM <= data.lessThanOrEqualTo
  );
  return archData || ARCH_HEIGHT_DATA_ORDERED[0];
};

/**
 * Reconstructs CustomInsoleValues from the reorder query params (set by the
 * "Reorder Personalized Insole" button on the customer account Profile
 * page), or returns null if a reorder wasn't requested / the params are
 * incomplete. `defaults` is spread in first so the result is a complete
 * CustomInsoleValues (typically pass the module's `initialInsoleValues`).
 */
export const buildReorderInsoleValues = (
  {
    reorderLeftFootLengthInMM,
    reorderRightFootLengthInMM,
    reorderLeftFootArchHeightInMM,
    reorderRightFootArchHeightInMM,
    reorderLeftEngraving,
    reorderRightEngraving,
    reorderFirstNameOnBox,
    reorderLastNameOnBox,
  }: QueryParams,
  defaults: CustomInsoleValues,
): CustomInsoleValues | null => {
  if (
    !reorderLeftFootLengthInMM ||
    !reorderRightFootLengthInMM ||
    !reorderLeftFootArchHeightInMM ||
    !reorderRightFootArchHeightInMM
  ) {
    return null;
  }

  const leftFootLengthInMM = parseFloat(reorderLeftFootLengthInMM);
  const rightFootLengthInMM = parseFloat(reorderRightFootLengthInMM);
  const leftFootArchHeightInMM = parseFloat(reorderLeftFootArchHeightInMM);
  const rightFootArchHeightInMM = parseFloat(reorderRightFootArchHeightInMM);

  if (
    isNaN(leftFootLengthInMM) ||
    isNaN(rightFootLengthInMM) ||
    isNaN(leftFootArchHeightInMM) ||
    isNaN(rightFootArchHeightInMM)
  ) {
    return null;
  }

  return {
    ...defaults,
    leftFootLengthInMM,
    rightFootLengthInMM,
    leftFootArchHeightInMM,
    rightFootArchHeightInMM,
    leftFootArchHeightData: getArchHeightData(leftFootArchHeightInMM),
    rightFootArchHeightData: getArchHeightData(rightFootArchHeightInMM),
    leftFootSizeData: getFootSize(leftFootLengthInMM) || {},
    rightFootSizeData: getFootSize(rightFootLengthInMM) || {},
    leftFootEngraving: reorderLeftEngraving || "",
    rightFootEngraving: reorderRightEngraving || "",
    firstNameOnBox: reorderFirstNameOnBox || "",
    lastNameOnBox: reorderLastNameOnBox || "",
  };
};

/**
 * Gets foot size data based on foot length in mm
 * @param footLengthInMM - Foot length measurement in millimeters
 * @returns FootSizeData object matching the measurement
 */
export const getFootSize = (footLengthInMM: number): FootSizeData | undefined => {
  const sizeData = Object.values(FOOT_SIZE_DATA).find(
    (data) => footLengthInMM >= data.greaterThanOrEqual && footLengthInMM < data.lessThan
  );
  return sizeData || undefined;
};

export const isFootSizeNotSold = (footLengthInMM: number): boolean => {
  return isFootSizeTooSmall(footLengthInMM) || isFootSizeTooLarge(footLengthInMM);
};

export const isFootSizeTooSmall = (footLengthInMM: number): boolean => {
  return footLengthInMM < FOOT_SIZE_DATA['5'].greaterThanOrEqual;
};

export const isFootSizeTooLarge = (footLengthInMM: number): boolean => {
  return footLengthInMM >= FOOT_SIZE_DATA['15'].lessThan;
};

/**
 * Maps weight in lbs to ribbing level (0-10)
 * Weight buckets 1-11 map to ribbing levels 0-10
 *
 * @param weightLbs - Weight in pounds
 * @returns Ribbing level from 0 (lightest) to 10 (heaviest)
 */
export const getRibbingLevelFromWeight = (weightLbs: number): number => {
  // Find the matching weight bucket
  for (const key in WEIGHT_DATA) {
    const range = WEIGHT_DATA[key];
    if (weightLbs >= range.greaterThanOrEqual && weightLbs < range.lessThan) {
      // Bucket 1-11 maps to level 0-10
      return range.bucket - 1;
    }
  }

  // Default to level 0 (lightest) if no match
  return 0;
};

/**
 * Gets ribbing level directly from weight bucket number
 *
 * @param bucket - Weight bucket (1-11)
 * @returns Ribbing level from 0 (lightest) to 10 (heaviest)
 */
export const getRibbingLevelFromBucket = (bucket: number): number => {
  return Math.max(0, Math.min(10, bucket - 1));
};

/**
 * Calculates the percentage position of an arch height measurement WITHIN a specific
 * pill bucket's mm range. The three pills cover:
 *   pillBucketBoundary=3 → buckets 1–3, range [0, 22mm]
 *   pillBucketBoundary=6 → buckets 4–6, range [22, 34mm]
 *   pillBucketBoundary=9 → buckets 7–9, range [34, 52mm]  (Infinity capped at greaterThan + 10)
 *
 * Using the pill's own range (instead of the global 14–42mm range) ensures the pointer
 * is positioned correctly relative to the pill div it is rendered inside.
 *
 * @param archHeightInMM - Arch height measurement in millimeters
 * @param pillBucketBoundary - The upper bucket boundary of the pill (3, 6 or 9)
 * @returns Percentage value (0–100) within the pill's mm range
 */
export const getArchHeightPercentageWithinPill = (
  archHeightInMM: number,
  pillBucketBoundary: number
): number => {
  const BUCKETS_PER_PILL = 3;
  const startBucketIdx = pillBucketBoundary - BUCKETS_PER_PILL + 1; // 1, 4, 7
  const endBucketIdx   = pillBucketBoundary;                         // 3, 6, 9

  const startBucketData = ARCH_HEIGHT_DATA[startBucketIdx.toString()];
  const endBucketData   = ARCH_HEIGHT_DATA[endBucketIdx.toString()];

  const minMM = startBucketIdx == 1 ? startBucketData.lessThanOrEqualTo : startBucketData.greaterThan;
  const maxMM = endBucketIdx == 9 ? endBucketData.greaterThan : endBucketData.lessThanOrEqualTo;

  const totalRange = maxMM - minMM;
  const percentage = ((archHeightInMM - minMM) / totalRange) * 100;

  return Math.max(0, Math.min(100, percentage));
};

/**
 * @deprecated Use getArchHeightPercentageWithinPill for per-pill positioning instead.
 */
export const getArchHeightSliderPercentage = (archHeightInMM: number): number => {
  const minArchHeightMM = ARCH_HEIGHT_DATA_ORDERED[0].lessThanOrEqualTo; // 14mm
  const maxArchHeightMM = ARCH_HEIGHT_DATA_ORDERED[ARCH_HEIGHT_DATA_ORDERED.length - 1].greaterThan; // 42mm
  const totalRange = maxArchHeightMM - minArchHeightMM;
  const percentage = ((archHeightInMM - minArchHeightMM) / totalRange) * 100;
  return Math.max(0, Math.min(100, percentage));
};

/**
 * Calculates the percentage position for foot size slider based on foot length mm value
 * Maps the foot length mm value to a percentage (0-100) based on the actual mm range
 * Minimum: 232.83mm (greaterThanOrEqual of size "5")
 * Maximum: 309.03mm (greaterThanOrEqual of size "15")
 *
 * @param footLengthInMM - Foot length measurement in millimeters
 * @returns Percentage value (0-100) representing the position on the slider
 */
export const getFootSizeSliderPercentage = (footLengthInMM: number): number => {
  // Define the min and max foot length values from FOOT_SIZE_DATA
  // Minimum: greaterThanOrEqual of size "5" = 232.83mm
  // Maximum: greaterThanOrEqual of size "15" = 309.03mm
  const minFootLengthMM = FOOT_SIZE_DATA["5"].greaterThanOrEqual; // 221mm
  const maxFootLengthMM = FOOT_SIZE_DATA["15"].lessThan; // 319mm

  // Calculate the total range
  const totalRange = maxFootLengthMM - minFootLengthMM; // 319 - 221 = 98mm

  // Calculate how far the current value is from the minimum
  const valueFromMin = footLengthInMM - minFootLengthMM;

  // Calculate the percentage (0-100)
  const percentage = (valueFromMin / totalRange) * 100;

  // Clamp between 0 and 100
  return Math.max(0, Math.min(100, percentage));
};

/**
 * Calculates the percentage position for foot size slider based on foot length mm value
 * within a dynamic size range (e.g., only showing sizes 7-12 instead of 5-15)
 *
 * @param footLengthInMM - Foot length measurement in millimeters
 * @param minSize - Minimum size number in the visible range (e.g., 7)
 * @param maxSize - Maximum size number in the visible range (e.g., 12)
 * @returns Percentage value (0-100) representing the position on the slider
 */
export const getFootSizeSliderPercentageInRange = (
  footLengthInMM: number,
  minFootLengthMM: number,
  maxFootLengthMM: number
): number => {
  // Calculate the total range
  const totalRange = maxFootLengthMM - minFootLengthMM;

  // Calculate how far the current value is from the minimum
  const valueFromMin = footLengthInMM - minFootLengthMM;

  // Calculate the percentage (0-100)
  const percentage = (valueFromMin / totalRange) * 100;

  // Clamp between 0 and 100
  return Math.max(0, Math.min(100, percentage));
};

/**
 * Sanitizes text input to allow only alphanumeric characters
 * @param text - Input text to sanitize
 * @param allowSpaces - Whether to allow spaces (default: false)
 * @returns Sanitized text with only alphanumeric characters (and spaces if allowed)
 */
export const sanitizeAlphanumeric = (text: string, allowSpaces: boolean = false): string => {
  const pattern = allowSpaces ? /[^a-zA-Z0-9 ]/g : /[^a-zA-Z0-9]/g;
  return text.replace(pattern, '');
};

/**
 * Sanitizes engraving text input to allow only alphanumeric characters, hyphens, and spaces.
 * @param text - Input text to sanitize
 * @param allowSpaces - Whether to allow spaces (default: false)
 * @returns Sanitized text with only allowed engraving characters (and spaces if allowed)
 */
export const sanitizeEngravingText = (text: string, allowSpaces: boolean = false): string => {
  const pattern = allowSpaces ? /[^a-zA-Z0-9\- ]/g : /[^a-zA-Z0-9\-]/g;
  return text.replace(pattern, '');
};

/**
 * Sanitizes a name field to allow alphanumeric characters, hyphens, dots, and spaces.
 * Does not trim — trimming should happen on blur so users can type multi-word names.
 * @param text - Input text to sanitize
 * @returns Sanitized text with only allowed name characters
 */
export const sanitizeNameField = (text: string): string => {
  return text.replace(/[^a-zA-Z0-9\-. ]/g, '');
};
